import React, { useState, useCallback } from 'react';
import { VideoUpload } from './VideoUpload';
import { applyApi } from '../../services/apply.api';
import { CreateRegistrationRequest } from '@packages/global';

interface RegistrationFormProps {
  onSuccess: (registrationId: string) => void;
}

const RELATIONSHIPS = [
  '爷爷与孙子/孙女',
  '奶奶与孙子/孙女',
  '外公与外孙/外孙女',
  '外婆与外孙/外孙女',
  '多人祖孙组合'
];

export function RegistrationForm({ onSuccess }: RegistrationFormProps) {
  const [formData, setFormData] = useState<Partial<CreateRegistrationRequest>>({
    grandparentName: '',
    grandparentAge: undefined,
    grandchildName: '',
    grandchildAge: undefined,
    relationship: '',
    city: '',
    contactName: '',
    contactPhone: '',
    contactWechat: '',
    contactEmail: '',
    story: '',
    videoUrl: '',
    videoOriginalName: '',
    consentGuardian: false,
    consentDisplay: false,
    consentRules: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validate = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.grandparentName?.trim()) {
      newErrors.grandparentName = '请输入祖辈姓名';
    }
    if (!formData.grandparentAge || formData.grandparentAge < 50) {
      newErrors.grandparentAge = '祖辈年龄必须大于等于50岁';
    }
    if (!formData.grandchildName?.trim()) {
      newErrors.grandchildName = '请输入孙辈姓名';
    }
    if (!formData.grandchildAge || formData.grandchildAge < 3 || formData.grandchildAge > 16) {
      newErrors.grandchildAge = '孙辈年龄必须在3-16岁之间';
    }
    if (!formData.relationship) {
      newErrors.relationship = '请选择祖孙关系';
    }
    if (!formData.city?.trim()) {
      newErrors.city = '请输入所在城市';
    }
    if (!formData.contactName?.trim()) {
      newErrors.contactName = '请输入联系人姓名';
    }
    if (!formData.contactPhone?.trim()) {
      newErrors.contactPhone = '请输入联系电话';
    }
    // 邮箱格式验证（可选字段，但如果填写则必须格式正确）
    if (formData.contactEmail && formData.contactEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contactEmail.trim())) {
        newErrors.contactEmail = '请输入有效的电子邮箱地址';
      }
    }
    if (!formData.videoUrl) {
      newErrors.videoUrl = '请上传参赛视频';
    }
    if (!formData.consentGuardian) {
      newErrors.consentGuardian = '必须同意监护人授权';
    }
    if (!formData.consentDisplay) {
      newErrors.consentDisplay = '必须同意展示授权';
    }
    if (!formData.consentRules) {
      newErrors.consentRules = '必须同意赛事规则';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const response = await applyApi.createRegistration({
        grandparentName: formData.grandparentName,
        grandparentAge: formData.grandparentAge,
        grandchildName: formData.grandchildName,
        grandchildAge: formData.grandchildAge,
        relationship: formData.relationship,
        city: formData.city,
        contactName: formData.contactName,
        contactPhone: formData.contactPhone,
        contactWechat: formData.contactWechat,
        contactEmail: formData.contactEmail,
        story: formData.story,
        videoUrl: formData.videoUrl,
        videoOriginalName: formData.videoOriginalName,
        consentGuardian: formData.consentGuardian,
        consentDisplay: formData.consentDisplay,
        consentRules: formData.consentRules
      });

      onSuccess(response.registrationId);
    } catch (err: any) {
      console.error('Submit failed:', err);
      setSubmitError(err?.message || '提交失败，请重试');
    } finally {
      setSubmitting(false);
    }
  }, [formData, validate, onSuccess]);

  const handleChange = useCallback((field: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field as keyof typeof newErrors];
        return newErrors;
      });
    }
  }, [errors]);

  const handleVideoChange = useCallback((url: string, originalName: string) => {
    setFormData(prev => ({ ...prev, videoUrl: url, videoOriginalName: originalName }));
    if (errors.videoUrl) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.videoUrl;
        return newErrors;
      });
    }
  }, [errors]);

  return (
    <form className="form card vivid-form" onSubmit={handleSubmit}>
      <div className="form-section">
        <h2>祖辈信息</h2>
        <div className="form-grid">
          <div>
            <label>祖辈姓名 *</label>
            <input
              type="text"
              value={formData.grandparentName || ''}
              onChange={e => handleChange('grandparentName', e.target.value)}
              placeholder="请输入祖辈姓名"
            />
            {errors.grandparentName && <span className="error">{errors.grandparentName}</span>}
          </div>
          <div>
            <label>祖辈年龄 *</label>
            <input
              type="number"
              value={formData.grandparentAge || ''}
              onChange={e => handleChange('grandparentAge', parseInt(e.target.value))}
              min="50"
              placeholder="50岁以上"
            />
            {errors.grandparentAge && <span className="error">{errors.grandparentAge}</span>}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h2>孙辈信息</h2>
        <div className="form-grid">
          <div>
            <label>孙辈姓名 *</label>
            <input
              type="text"
              value={formData.grandchildName || ''}
              onChange={e => handleChange('grandchildName', e.target.value)}
              placeholder="请输入孙辈姓名"
            />
            {errors.grandchildName && <span className="error">{errors.grandchildName}</span>}
          </div>
          <div>
            <label>孙辈年龄 *</label>
            <input
              type="number"
              value={formData.grandchildAge || ''}
              onChange={e => handleChange('grandchildAge', parseInt(e.target.value))}
              min="3"
              max="16"
              placeholder="3-16岁"
            />
            {errors.grandchildAge && <span className="error">{errors.grandchildAge}</span>}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h2>组合信息</h2>
        <div className="form-grid">
          <div>
            <label>祖孙关系 *</label>
            <select
              value={formData.relationship || ''}
              onChange={e => handleChange('relationship', e.target.value)}
            >
              <option value="">请选择</option>
              {RELATIONSHIPS.map(r => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            {errors.relationship && <span className="error">{errors.relationship}</span>}
          </div>
          <div>
            <label>所在城市 *</label>
            <input
              type="text"
              value={formData.city || ''}
              onChange={e => handleChange('city', e.target.value)}
              placeholder="如：广州"
            />
            {errors.city && <span className="error">{errors.city}</span>}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h2>联系人信息</h2>
        <div className="form-grid">
          <div>
            <label>联系人姓名 *</label>
            <input
              type="text"
              value={formData.contactName || ''}
              onChange={e => handleChange('contactName', e.target.value)}
              placeholder="请输入联系人姓名"
            />
            {errors.contactName && <span className="error">{errors.contactName}</span>}
          </div>
          <div>
            <label>联系电话 *</label>
            <input
              type="tel"
              value={formData.contactPhone || ''}
              onChange={e => handleChange('contactPhone', e.target.value)}
              placeholder="请输入联系电话"
            />
            {errors.contactPhone && <span className="error">{errors.contactPhone}</span>}
          </div>
          <div>
            <label>微信号</label>
            <input
              type="text"
              value={formData.contactWechat || ''}
              onChange={e => handleChange('contactWechat', e.target.value)}
              placeholder="请输入微信号"
            />
          </div>
          <div>
            <label>电子邮箱</label>
            <input
              type="email"
              value={formData.contactEmail || ''}
              onChange={e => handleChange('contactEmail', e.target.value)}
              placeholder="请输入电子邮箱"
            />
            {errors.contactEmail && <span className="error">{errors.contactEmail}</span>}
          </div>
        </div>
      </div>

      <div className="form-section">
        <h2>祖孙故事</h2>
        <label>请简单介绍你们的祖孙故事、参赛初衷或视频亮点</label>
        <textarea
          value={formData.story || ''}
          onChange={e => handleChange('story', e.target.value)}
          rows={5}
          placeholder="建议 50-300 字"
        />
      </div>

      <div className="form-section upload-zone">
        <h2>视频上传</h2>
        <label>上传参赛视频（支持 MP4 / MOV / M4V，建议 60-180 秒，单文件 ≤ 220MB） *</label>
        <VideoUpload
          value={formData.videoUrl}
          onChange={handleVideoChange}
        />
        {errors.videoUrl && <span className="error">{errors.videoUrl}</span>}
      </div>

      <div className="form-section">
        <h2>授权与确认</h2>
        <div className="checks">
          <label>
            <input
              type="checkbox"
              checked={formData.consentGuardian || false}
              onChange={e => handleChange('consentGuardian', e.target.checked)}
            />
            我已获得儿童监护人授权，同意参赛与使用儿童肖像 *
          </label>
          {errors.consentGuardian && <span className="error">{errors.consentGuardian}</span>}

          <label>
            <input
              type="checkbox"
              checked={formData.consentDisplay || false}
              onChange={e => handleChange('consentDisplay', e.target.checked)}
            />
            我同意主办方在赛事展示、传播与评审中使用本作品 *
          </label>
          {errors.consentDisplay && <span className="error">{errors.consentDisplay}</span>}

          <label>
            <input
              type="checkbox"
              checked={formData.consentRules || false}
              onChange={e => handleChange('consentRules', e.target.checked)}
            />
            我已阅读并同意赛事规则、隐私保护与内容审核要求 *
          </label>
          {errors.consentRules && <span className="error">{errors.consentRules}</span>}
        </div>
      </div>

      {submitError && (
        <div className="submit-error">{submitError}</div>
      )}

      <div className="submit-row">
        <button className="btn primary" type="submit" disabled={submitting}>
          {submitting ? '提交中...' : '提交报名'}
        </button>
      </div>
    </form>
  );
}
